import React, { useEffect, useMemo, useState } from 'react';
import {
    Alert,
    Box,
    Button,
    Card,
    CardContent,
    Chip,
    CircularProgress,
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableRow,
    TextField,
    ToggleButton,
    ToggleButtonGroup,
    Tooltip,
    Typography,
} from '@mui/material';
import { FormattedMessage, useIntl } from 'react-intl';
import TipsAndUpdatesOutlinedIcon from '@mui/icons-material/TipsAndUpdatesOutlined';
import ChatBubbleOutlineIcon from '@mui/icons-material/ChatBubbleOutline';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import insightsApi from '../../api/insightsApi';
import GroupScoresChat, { historyOf, threadKey, useScopedChats } from './GroupScoresChat';
import GroupActionsCard, { freshActionsEntry } from './GroupActionsCard';
import GroupImpactsPanel from './GroupImpactsPanel';

// ── The "By group" tab ────────────────────────────────────────────────────────
// The same pillar signal bars as "Change Signals by Pillar", re-scored by the
// server for three nested populations of ONE wave:
//
//   baseline  the whole wave — the dashed "org" marker on every other bar
//   areas     each area, scored over the union of its groups' respondents
//   groups    each registered stakeholder group
//
// Two metrics per slice. Only risk is shown for now: the switch between them
// is hidden behind METRIC_SWITCH_ENABLED, and the tone path is kept intact so
// turning it back on is a one-line change.
//
//   risk       `pillar_change` — the risk model's pillar signal (Risks tab)
//   sentiment  `pillar_sentiment` — the average tone of what the slice wrote,
//              the Topics tab's "Change Signals by Pillar" re-cut; the server
//              guarantees the whole wave's row equals that tab's numbers
//
// Values arrive as signed percentages (risk clamped to ±100, tone already in
// range), so they are rendered as-is. A wave without stored tone says so with
// `has_sentiment: false`, and the switch then stays on risk. Slices under 5
// respondents never reach `areas`/`groups`; they are only named in `hidden`,
// which is why nothing here can show them.

const BASELINE = 'baseline';

export const METRIC_RISK = 'risk';
export const METRIC_SENTIMENT = 'sentiment';

// The payload field each metric reads on every slice.
export const METRIC_FIELD = {
    [METRIC_RISK]: 'pillar_change',
    [METRIC_SENTIMENT]: 'pillar_sentiment',
};

// Off: the tab is risk-only and the Risk / Change-signal switch is not drawn.
// Flip to true to bring the switch (and the tone metric) back.
export const METRIC_SWITCH_ENABLED = false;

// The metric actually shown: tone only when this wave has it.
export const effectiveMetric = (payload, metric) =>
    (metric === METRIC_SENTIMENT && payload?.has_sentiment ? METRIC_SENTIMENT : METRIC_RISK);

// Mockup bands: green for positive, orange for a small negative, red beyond.
const TONES = {
    pos: { bar: '#8fc45b', pillBg: '#e6f2d7', pillFg: '#4b7d24' },
    warn: { bar: '#f4a84a', pillBg: '#fdeccf', pillFg: '#b56c14' },
    neg: { bar: '#ee7b7b', pillBg: '#fde3e3', pillFg: '#c53434' },
};

export const toneFor = (value) => {
    if (value > 0) return 'pos';
    return Math.abs(value) < 15 ? 'warn' : 'neg';
};

// The server's risk caption leads with "<pillar> <band>", its band word in the
// report's language (BE `RISK_WORDS`): Critical / Elevated / Low in English,
// Crítico / Elevado / Bajo in Spanish. English is matched as it always was;
// Spanish case-insensitively, as a whole word, accent optional. Low / Bajo is
// the green default. (A band key sent as data would make this matching
// unnecessary.)
const CRITICAL_ES = /\bcr[ií]tico\b/i;
const ELEVATED_ES = /\belevado\b/i;

export const riskColor = (risk) => {
    const text = risk || '';
    if (/Critical/.test(text) || CRITICAL_ES.test(text)) return TONES.neg.pillFg;
    if (/Elevated/.test(text) || ELEVATED_ES.test(text)) return TONES.warn.pillFg;
    return TONES.pos.pillFg;
};

const signed = (value, digits) => `${value > 0 ? '+' : ''}${Number(value).toFixed(digits)}`;
// The same, with a typographic minus for display text.
const signedText = (value, digits) => signed(value, digits).replace('-', '−');

// The tab's palette, in the Risks tab's blues.
const INK = '#16335c';
const TEXT = '#33445c';
const MUTED = '#6b7a90';
const FAINT = '#8493a6';
const LINE = '#e6ebf2';
const TINT = '#f7f9fc';
const ACCENT = '#1565c0';
const ACCENT_DARK = '#0d47a1';
const NUMERIC = { fontVariantNumeric: 'tabular-nums' };

// A small uppercase section label, with an optional right-hand slot.
function SectionTitle({ children, action, sx }) {
    return (
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 1.5, flexWrap: 'wrap', ...sx }}>
            <Typography component="h3" sx={{ fontSize: 12.5, color: MUTED, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '.05em' }}>
                {children}
            </Typography>
            {action}
        </Box>
    );
}

// A section below the first: one rule and one rhythm for all of them.
const SECTION_SX = { mt: 3.5, pt: 2.5, borderTop: `1px solid ${LINE}` };

// Whether a payload scored anything below the whole wave. The report page
// shows the tab only when this holds, so an empty registry or a failed fetch
// (null) leaves it out.
export const hasGroupSlices = (payload) =>
    ((payload?.areas?.length || 0) + (payload?.groups?.length || 0)) > 0;

// "Where the gaps are": a fixed overview that ignores the selector, computed
// for one metric (`field`: 'pillar_change' or 'pillar_sentiment').
//   rows        one per pillar, in the payload's order: the weakest and the
//               strongest AREA and the gap between them (null with < 2 areas)
//   mostAtRisk  the single lowest group × pillar cell
//   widest      the pillar whose areas are furthest apart
// The baseline is never a candidate; hidden slices are not in the payload.
export function summarizeGaps(payload, field = METRIC_FIELD[METRIC_RISK]) {
    const areas = payload?.areas || [];
    const groups = payload?.groups || [];
    const pillars = payload?.pillars?.length
        ? payload.pillars
        : Object.keys(payload?.baseline?.[field] || {});

    const rows = pillars.map((pillar) => {
        const scored = areas
            .map((area) => ({ name: area.name, value: (area[field] || {})[pillar] }))
            .filter((entry) => typeof entry.value === 'number');
        if (scored.length === 0) return { pillar, weakest: null, strongest: null, gap: null };
        const weakest = scored.reduce((low, entry) => (entry.value < low.value ? entry : low));
        const strongest = scored.reduce((high, entry) => (entry.value > high.value ? entry : high));
        if (scored.length === 1) return { pillar, weakest, strongest: null, gap: null };
        return { pillar, weakest, strongest, gap: strongest.value - weakest.value };
    });

    let mostAtRisk = null;
    groups.forEach((group) => {
        pillars.forEach((pillar) => {
            const value = (group[field] || {})[pillar];
            if (typeof value === 'number' && (!mostAtRisk || value < mostAtRisk.value)) {
                mostAtRisk = { name: group.name, pillar, value };
            }
        });
    });

    const widest = rows
        .filter((row) => row.gap !== null)
        .reduce((best, row) => (!best || row.gap > best.gap ? row : best), null);

    return { rows, mostAtRisk, widest };
}

// Position on a track centred at zero: -100% → 0, 0 → 50, +100% → 100.
const trackPosition = (value) => 50 + Math.max(-100, Math.min(100, value)) / 2;

// Resolve a selector value ("baseline", "area:2", "group:5") to what the page
// shows: the payload row plus the level it came from.
function resolveSelection(data, key) {
    if (!data) return null;
    const [level, index] = key.split(':');
    const row = level === 'area' ? data.areas?.[Number(index)]
        : level === 'group' ? data.groups?.[Number(index)]
            : null;
    if (row) return { level, row };
    return { level: BASELINE, row: data.baseline || {} };
}

function ScorePill({ value }) {
    const tone = TONES[toneFor(value)];
    return (
        <Box component="span" sx={{ ...NUMERIC, fontWeight: 700, fontSize: 12, px: 1, py: 0.25, borderRadius: 999, bgcolor: tone.pillBg, color: tone.pillFg, whiteSpace: 'nowrap' }}>
            {signedText(value, 1)}
        </Box>
    );
}

function AreaCell({ entry }) {
    if (!entry) return <Box component="span" sx={{ color: 'text.disabled' }}>—</Box>;
    return (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
            <span>{entry.name}</span>
            <ScorePill value={entry.value} />
        </Box>
    );
}

// Fixed across selections, so it sits apart from the selection-driven content.
function GapsSummary({ payload, metric }) {
    const { rows, mostAtRisk, widest } = summarizeGaps(payload, METRIC_FIELD[metric]);
    if (!(payload?.areas || []).length && !mostAtRisk) return null;
    const headSx = { fontSize: 12.5, fontWeight: 600, color: ACCENT_DARK, bgcolor: 'rgba(21, 101, 192, 0.06)', whiteSpace: 'nowrap' };
    const highlightSx = { px: 1.5, py: 1, bgcolor: TINT, border: `1px solid ${LINE}`, borderRadius: 2, fontSize: 13.5, color: TEXT };
    return (
        <Box data-testid="group-gaps-summary" sx={SECTION_SX}>
            <SectionTitle>
                <FormattedMessage id="groupScores.gaps_title" defaultMessage="Where the gaps are" />
            </SectionTitle>
            <Typography variant="caption" component="p" sx={{ color: FAINT, mt: 0.25, mb: 1.5 }}>
                <FormattedMessage id="groupScores.gaps_subtitle" defaultMessage="Across all areas, for the metric shown — this does not change with the selection above." />
            </Typography>

            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mb: 1.75, '& > span': highlightSx }}>
                {mostAtRisk && (
                    <span data-testid="group-most-at-risk">
                        {metric === METRIC_SENTIMENT ? (
                            <FormattedMessage
                                id="groupScores.most_negative_tone"
                                defaultMessage="Most negative tone: {group} on {pillar} ({score})"
                                values={{ group: <b>{mostAtRisk.name}</b>, pillar: <b>{mostAtRisk.pillar}</b>, score: signedText(mostAtRisk.value, 1) }}
                            />
                        ) : (
                            <FormattedMessage
                                id="groupScores.most_at_risk"
                                defaultMessage="Most at-risk: {group} on {pillar} ({score})"
                                values={{ group: <b>{mostAtRisk.name}</b>, pillar: <b>{mostAtRisk.pillar}</b>, score: signedText(mostAtRisk.value, 1) }}
                            />
                        )}
                    </span>
                )}
                {widest && (
                    <span data-testid="group-widest-split">
                        <FormattedMessage
                            id="groupScores.widest_split"
                            defaultMessage="Widest split: {pillar}, {gap} pts across areas"
                            values={{ pillar: <b>{widest.pillar}</b>, gap: Math.round(widest.gap) }}
                        />
                    </span>
                )}
            </Box>

            {(payload?.areas || []).length > 0 && (
                <Box sx={{ overflowX: 'auto', border: `1px solid ${LINE}`, borderRadius: 2.5 }}>
                    <Table
                        size="small"
                        sx={{
                            minWidth: { xs: 0, sm: 520 },
                            '& td, & th': { borderColor: LINE, fontSize: 13, py: 1 },
                            '& tbody tr:nth-of-type(even) td': { bgcolor: '#fafbfd' },
                            '& tbody tr:last-of-type td': { borderBottom: 0 },
                        }}
                    >
                        <TableHead>
                            <TableRow>
                                <TableCell sx={headSx}><FormattedMessage id="groupScores.gaps_pillar" defaultMessage="Pillar" /></TableCell>
                                <TableCell sx={headSx}><FormattedMessage id="groupScores.gaps_weakest" defaultMessage="Weakest area" /></TableCell>
                                <TableCell sx={headSx}><FormattedMessage id="groupScores.gaps_strongest" defaultMessage="Strongest area" /></TableCell>
                                <TableCell sx={headSx} align="right"><FormattedMessage id="groupScores.gaps_gap" defaultMessage="Gap" /></TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {rows.map((row) => (
                                <TableRow key={row.pillar} data-testid={`group-gap-row-${row.pillar}`}>
                                    <TableCell sx={{ fontWeight: 600, color: TEXT, whiteSpace: 'nowrap' }}>{row.pillar}</TableCell>
                                    <TableCell><AreaCell entry={row.weakest} /></TableCell>
                                    <TableCell><AreaCell entry={row.strongest} /></TableCell>
                                    <TableCell align="right" sx={{ ...NUMERIC, fontWeight: 700, color: INK, whiteSpace: 'nowrap' }}>
                                        {row.gap === null ? '' : (
                                            <FormattedMessage id="groupScores.gap_points" defaultMessage="{gap} pts" values={{ gap: Math.round(row.gap) }} />
                                        )}
                                    </TableCell>
                                </TableRow>
                            ))}
                        </TableBody>
                    </Table>
                </Box>
            )}
        </Box>
    );
}

// The hidden reason the server gives a registered group nobody was placed in.
// Every other reason is "fewer than 5 respondents".
const NO_MEMBERS = 'no members';

// Groups the server left out, by reason. Shared by the tab and its empty state.
function HiddenCaptions({ hidden }) {
    const tooSmall = (hidden || []).filter((h) => h.reason !== NO_MEMBERS);
    const memberless = (hidden || []).filter((h) => h.reason === NO_MEMBERS);
    return (
        <>
            {tooSmall.length > 0 && (
                <Typography data-testid="group-hidden-caption" variant="caption" component="p" sx={{ mt: 2, color: '#8493a6' }}>
                    <FormattedMessage
                        id="groupScores.hidden_caption"
                        defaultMessage="{count, plural, one {# group hidden} other {# groups hidden}} — fewer than 5 respondents: {names}"
                        values={{ count: tooSmall.length, names: tooSmall.map((h) => h.name).join(', ') }}
                    />
                </Typography>
            )}
            {memberless.length > 0 && (
                <Typography data-testid="group-no-members-caption" variant="caption" component="p" sx={{ mt: 0.5, color: '#8493a6' }}>
                    <FormattedMessage
                        id="groupScores.no_members_caption"
                        defaultMessage="{count, plural, one {# group not scored} other {# groups not scored}} — no members: {names}"
                        values={{ count: memberless.length, names: memberless.map((h) => h.name).join(', ') }}
                    />
                </Typography>
            )}
        </>
    );
}

// Named for what it measures, so the two metrics can never be mistaken for
// each other: the risk model's pillar signal (the Risks tab's metric), or the
// Topics tab's "Change Signals by Pillar" tone.
function Heading({ metric = METRIC_RISK }) {
    const sentiment = metric === METRIC_SENTIMENT;
    return (
        <>
            <Typography variant="h6" data-testid="group-scores-title" sx={{ color: INK, fontWeight: 600 }}>
                {sentiment
                    ? <FormattedMessage id="groupScores.title_sentiment" defaultMessage="Change signals by pillar — by group" />
                    : <FormattedMessage id="groupScores.title" defaultMessage="Pillar signal scores — by group" />}
            </Typography>
            <Typography variant="body2" color="text.secondary" data-testid="group-scores-note" sx={{ mt: 0.25 }}>
                {sentiment
                    ? <FormattedMessage id="groupScores.sentiment_note" defaultMessage="Average tone of what each group wrote — the same metric as the Topics tab." />
                    : <FormattedMessage id="groupScores.model_note" defaultMessage="What the risk model predicts from the signals in each group's responses — the same metric as the Risks tab." />}
            </Typography>
        </>
    );
}

const SWITCH_SX = {
    bgcolor: '#fff',
    '& .MuiToggleButton-root': {
        textTransform: 'none',
        fontWeight: 600,
        height: 40,
        px: 1.75,
        borderColor: 'rgba(21, 101, 192, 0.2)',
        color: 'text.secondary',
        fontSize: 12,
    },
    '& .MuiToggleButton-root.Mui-selected': {
        bgcolor: 'rgba(21, 101, 192, 0.12)',
        color: '#0d47a1',
        borderColor: 'rgba(21, 101, 192, 0.45)',
    },
};

// Risk <-> tone. Tone is disabled, with a reason, on a wave that has none.
function MetricSwitch({ metric, available, onChange, unavailableReason }) {
    const intl = useIntl();
    const sentimentButton = (
        <ToggleButton value={METRIC_SENTIMENT} disabled={!available} data-testid="group-metric-sentiment">
            <FormattedMessage id="groupScores.metric_sentiment" defaultMessage="Change signals (tone)" />
        </ToggleButton>
    );
    return (
        <ToggleButtonGroup
            exclusive
            size="small"
            value={metric}
            onChange={(_, next) => { if (next) onChange(next); }}
            aria-label={intl.formatMessage({ id: 'groupScores.metric_label', defaultMessage: 'Metric' })}
            sx={SWITCH_SX}
        >
            <ToggleButton value={METRIC_RISK} data-testid="group-metric-risk">
                <FormattedMessage id="groupScores.metric_risk" defaultMessage="Pillar signal scores" />
            </ToggleButton>
            {available ? sentimentButton : (
                <Tooltip title={unavailableReason || intl.formatMessage({ id: 'groupScores.sentiment_unavailable', defaultMessage: 'Not available for this wave yet' })}>
                    <span data-testid="group-metric-sentiment-unavailable">{sentimentButton}</span>
                </Tooltip>
            )}
        </ToggleButtonGroup>
    );
}

// The centred-zero track for one value, with the optional dashed org marker.
// `thin` is the compare view's pair of bars under one pillar.
// `markerLabel` prints "org" over the marker — the first row only, so it names
// the line once instead of on every pillar.
function SignalTrack({ value, baseline, thin = false, markerLabel = false }) {
    const hasValue = typeof value === 'number';
    const tone = TONES[toneFor(hasValue ? value : 0)];
    const start = hasValue && value < 0 ? trackPosition(value) : 50;
    const width = hasValue ? Math.abs(trackPosition(value) - 50) : 0;
    return (
        <Box sx={{ position: 'relative', height: thin ? 18 : 24, bgcolor: '#eef2f7', borderRadius: 1.5 }}>
            <Box sx={{ position: 'absolute', left: '50%', top: -2, bottom: -2, width: 2, bgcolor: '#5b6b82', opacity: 0.75, borderRadius: 1, transform: 'translateX(-50%)', zIndex: 2 }} />
            {typeof baseline === 'number' && (
                <Box
                    data-testid="group-baseline-marker"
                    sx={{
                        position: 'absolute',
                        left: `${trackPosition(baseline)}%`,
                        top: -3,
                        bottom: -3,
                        borderLeft: '2px dashed #33445c',
                        opacity: 0.6,
                        zIndex: 1,
                    }}
                >
                    {markerLabel && (
                        <Typography component="span" sx={{ position: 'absolute', top: -16, left: 0, transform: 'translateX(-50%)', fontSize: 10, fontWeight: 600, color: TEXT, lineHeight: 1 }}>
                            <FormattedMessage id="groupScores.baseline_marker" defaultMessage="org" />
                        </Typography>
                    )}
                </Box>
            )}
            {hasValue && (
                <Box
                    sx={{
                        position: 'absolute',
                        top: thin ? 3 : 4,
                        height: thin ? 12 : 16,
                        left: `${start}%`,
                        width: `${width}%`,
                        bgcolor: tone.bar,
                        // round the outer end only; the zero end meets the axis square
                        borderRadius: value < 0 ? '4px 0 0 4px' : '0 4px 4px 0',
                        transition: 'left 0.3s ease, width 0.3s ease',
                    }}
                />
            )}
        </Box>
    );
}

function ValuePill({ value, testId, small = false }) {
    const hasValue = typeof value === 'number';
    const tone = TONES[toneFor(hasValue ? value : 0)];
    return (
        <Box
            data-testid={testId}
            sx={{
                ...NUMERIC,
                textAlign: 'center',
                fontSize: small ? 12 : 12.5,
                fontWeight: 700,
                py: small ? 0.25 : 0.5,
                borderRadius: 999,
                bgcolor: hasValue ? tone.pillBg : 'transparent',
                color: hasValue ? tone.pillFg : 'text.disabled',
            }}
        >
            {hasValue ? `${signed(value, 2)}%` : '—'}
        </Box>
    );
}

// The selection's precomputed AI focus areas — the AI read of the chart, right
// under the bars and above the themes and gaps. `children` (the chat toggle
// and, once opened, the chat) sits at the foot of the card. The whole wave,
// every area and every group carry one; a selection without a stored summary
// (`summary_status: "none"`) simply shows no card. One narrative per
// selection, so it does NOT follow the metric. Single view only.
export const focusAreasOf = (row) =>
    (row?.summary_status === 'ready' && Array.isArray(row.focus_areas) ? row.focus_areas : [])
        .filter((item) => item && item.headline);

function FocusAreasCard({ items, children, title, subtitle, testId = 'group-focus-areas' }) {
    return (
        <Box
            data-testid={testId}
            sx={{
                px: { xs: 2, sm: 2.75 },
                py: { xs: 2, sm: 2.25 },
                borderRadius: 3,
                border: '1px solid rgba(21, 101, 192, 0.14)',
                background: 'linear-gradient(135deg, #f8faff 0%, #eef3fb 100%)',
                boxShadow: '0 8px 18px rgba(13, 71, 161, 0.06)',
            }}
        >
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
                <TipsAndUpdatesOutlinedIcon fontSize="small" sx={{ color: ACCENT }} />
                <Typography component="h3" sx={{ fontSize: 15, fontWeight: 700, color: ACCENT_DARK }}>
                    {title || <FormattedMessage id="groupScores.focus_title" defaultMessage="Focus areas" />}
                </Typography>
            </Box>
            {subtitle && (
                <Typography data-testid={`${testId}-sides`} variant="body2" sx={{ color: MUTED, fontWeight: 600, mb: 0.5 }}>
                    {subtitle}
                </Typography>
            )}
            <Box component="ol" sx={{ m: 0, p: 0, listStyle: 'none' }}>
                {items.map((item, i) => (
                    <Box
                        component="li"
                        key={`${i}-${item.headline}`}
                        data-testid="group-focus-item"
                        sx={{ display: 'flex', gap: 1.5, py: 1.75, '& + li': { borderTop: '1px solid rgba(21, 101, 192, 0.12)' }, '&:last-of-type': { pb: 0.5 } }}
                    >
                        <Box
                            component="span"
                            sx={{
                                flexShrink: 0, mt: '1px', width: 24, height: 24, borderRadius: '50%',
                                display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
                                fontSize: 12, fontWeight: 700, bgcolor: ACCENT, color: '#fff',
                            }}
                        >
                            {i + 1}
                        </Box>
                        <Box sx={{ minWidth: 0, flex: 1 }}>
                            <Typography sx={{ fontSize: 15.5, fontWeight: 700, color: INK, lineHeight: 1.4 }}>
                                {item.headline}
                            </Typography>
                            {item.detail && (
                                <Typography variant="body2" sx={{ color: '#47566b', mt: 0.5, lineHeight: 1.6 }}>
                                    {item.detail}
                                </Typography>
                            )}
                            {String(item.action || '').trim() && (
                                <Typography
                                    data-testid="group-focus-action"
                                    variant="body2"
                                    sx={{ color: TEXT, mt: 1, px: 1.5, py: 0.9, bgcolor: '#fff', border: '1px solid rgba(21, 101, 192, 0.14)', borderRadius: 2, lineHeight: 1.5 }}
                                >
                                    <Box component="span" sx={{ fontWeight: 700, color: ACCENT }}>
                                        <FormattedMessage id="groupScores.focus_action" defaultMessage="Suggested action:" />
                                    </Box>{' '}
                                    {String(item.action).trim()}
                                </Typography>
                            )}
                            {(item.pillars || []).length > 0 && (
                                <Box sx={{ display: 'flex', gap: 0.75, flexWrap: 'wrap', mt: 1.1 }}>
                                    {item.pillars.map((pillar) => (
                                        <Chip
                                            key={pillar}
                                            size="small"
                                            variant="outlined"
                                            label={pillar}
                                            sx={{ height: 22, fontSize: 11.5, fontWeight: 600, bgcolor: '#fff', color: ACCENT, borderColor: 'rgba(21, 101, 192, 0.28)' }}
                                        />
                                    ))}
                                </Box>
                            )}
                        </Box>
                    </Box>
                ))}
            </Box>
            {children}
        </Box>
    );
}

// The in-card chat, collapsed behind a button. Unmounted while closed; the
// thread itself lives in useScopedChats, so closing loses nothing.
function ChatToggle({ open, onToggle, children }) {
    return (
        <Box sx={{ mt: 2 }}>
            <Button
                data-testid="group-chat-toggle"
                size="small"
                variant={open ? 'text' : 'outlined'}
                aria-expanded={open}
                onClick={onToggle}
                startIcon={open ? <ExpandLessIcon fontSize="small" /> : <ChatBubbleOutlineIcon fontSize="small" />}
                sx={{ textTransform: 'none', fontWeight: 600, borderRadius: 2, bgcolor: open ? 'transparent' : '#fff' }}
            >
                {open
                    ? <FormattedMessage id="groupScores.chat_hide" defaultMessage="Hide chat" />
                    : <FormattedMessage id="groupScores.chat_toggle" defaultMessage="Chat about this" />}
            </Button>
            {open && children}
        </Box>
    );
}

// ── Compare insight ──────────────────────────────────────────────────────────
// A compare view's on-demand AI read: never loaded until "Show AI insights"
// is pressed, read-only (no chat). `state` is this pair's entry in the
// parent's map — undefined | { loading } | { data } | { error } — so a pair
// already loaded shows again without a refetch. `disabledReason`, when set,
// disables the button (a side hidden or unscored in its wave).
export const comparePairKey = (reportId, body) =>
    [body.mode, reportId, body.scope_a, body.scope_b ?? '', body.other_report_id ?? ''].join('|');

// The BE stores a pair canonically (groups sorted, waves earlier first), so
// groups are put back in the viewer's A / B order for the label.
function compareSidesLabel(data, intl, body) {
    const sides = [...(data?.sides || [])];
    if (data?.mode === 'groups' && body) {
        sides.sort((x, y) => Number(y.scope_key === body.scope_a) - Number(x.scope_key === body.scope_a));
    }
    if (data?.mode === 'waves' && sides.length === 2) {
        return intl.formatMessage(
            { id: 'groupScores.compare_insight_waves', defaultMessage: '{name} · Wave {a} → Wave {b}' },
            { name: sides[0].name, a: sides[0].wave_count, b: sides[1].wave_count },
        );
    }
    return sides.map((side) => side.name).join(intl.formatMessage({ id: 'groupScores.compare_insight_vs', defaultMessage: ' vs ' }));
}

function CompareInsight({ state, body, disabledReason, onLoad }) {
    const intl = useIntl();
    const items = focusAreasOf({ summary_status: 'ready', focus_areas: state?.data?.focus_areas });
    return (
        <Box data-testid="group-compare-insight-panel" sx={{ mt: 3 }}>
            {state?.data ? (
                items.length > 0 ? (
                    <FocusAreasCard
                        testId="group-compare-insight"
                        items={items}
                        title={<FormattedMessage id="groupScores.compare_insight_title" defaultMessage="AI insights" />}
                        subtitle={compareSidesLabel(state.data, intl, body)}
                    />
                ) : (
                    <Typography data-testid="group-compare-insight-empty" variant="body2" sx={{ color: FAINT }}>
                        <FormattedMessage id="groupScores.compare_insight_empty" defaultMessage="No insights came back for this comparison." />
                    </Typography>
                )
            ) : (
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, flexWrap: 'wrap' }}>
                    <Tooltip title={disabledReason || ''} disableHoverListener={!disabledReason}>
                        <span>
                            <Button
                                data-testid="group-compare-insight-button"
                                variant="outlined"
                                size="small"
                                disabled={Boolean(disabledReason) || Boolean(state?.loading)}
                                onClick={onLoad}
                                startIcon={state?.loading
                                    ? <CircularProgress size={14} data-testid="group-compare-insight-loading" />
                                    : <TipsAndUpdatesOutlinedIcon fontSize="small" />}
                                sx={{ textTransform: 'none', fontWeight: 600, borderRadius: 2, bgcolor: '#fff' }}
                            >
                                {state?.loading
                                    ? <FormattedMessage id="groupScores.compare_insight_loading" defaultMessage="Generating insights…" />
                                    : <FormattedMessage id="groupScores.compare_insight_button" defaultMessage="Show AI insights" />}
                            </Button>
                        </span>
                    </Tooltip>
                    {disabledReason && (
                        <Typography data-testid="group-compare-insight-disabled" variant="caption" sx={{ color: FAINT }}>
                            {disabledReason}
                        </Typography>
                    )}
                    {state?.loading && (
                        <Typography variant="caption" sx={{ color: FAINT }}>
                            <FormattedMessage id="groupScores.compare_insight_wait" defaultMessage="The first time for a pair can take a few seconds." />
                        </Typography>
                    )}
                    {state?.error && (
                        <Box data-testid="group-compare-insight-error" sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <Typography variant="body2" sx={{ color: TONES.neg.pillFg }}>
                                {state.error.status === 404
                                    ? <FormattedMessage id="groupScores.compare_insight_unavailable" defaultMessage="Insights aren't available for this pair." />
                                    : <FormattedMessage id="groupScores.compare_insight_failed" defaultMessage="Couldn't load the insights." />}
                            </Typography>
                            <Button data-testid="group-compare-insight-retry" size="small" onClick={onLoad} sx={{ textTransform: 'none', fontWeight: 600 }}>
                                <FormattedMessage id="groupScores.compare_insight_retry" defaultMessage="Retry" />
                            </Button>
                        </Box>
                    )}
                </Box>
            )}
        </Box>
    );
}

// Label | track | value, on one grid for every row so the columns line up.
const PILLAR_GRID = { xs: '88px minmax(0, 1fr) 70px', sm: '160px minmax(0, 1fr) 88px' };
const COMPARE_GRID = { xs: '88px minmax(0, 1fr)', sm: '160px minmax(0, 1fr)' };

function PillarRow({ pillar, value, baseline, markerLabel }) {
    return (
        <Box
            data-testid={`group-pillar-${pillar}`}
            sx={{
                display: 'grid',
                gridTemplateColumns: PILLAR_GRID,
                alignItems: 'center',
                columnGap: { xs: 1.25, sm: 2 },
                py: 1.1,
                '& + &': { borderTop: '1px solid #f1f4f8' },
            }}
        >
            <Typography variant="body2" sx={{ fontWeight: 600, color: TEXT, textAlign: 'right', lineHeight: 1.25 }}>
                {pillar}
            </Typography>
            <SignalTrack value={value} baseline={baseline} markerLabel={markerLabel} />
            <ValuePill value={value} testId={`group-pillar-value-${pillar}`} />
        </Box>
    );
}

// Colour bands and the org marker, shown with every set of bars.
function Legend({ metric = METRIC_RISK }) {
    return (
        <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', alignItems: 'center', fontSize: 11.5, color: FAINT }}>
            <Box component="span" sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.75 }}>
                <Box component="span" sx={{ display: 'inline-flex', borderRadius: 0.5, overflow: 'hidden' }}>
                    {['neg', 'warn', 'pos'].map((t) => (
                        <Box key={t} component="span" sx={{ width: 8, height: 9, bgcolor: TONES[t].bar }} />
                    ))}
                </Box>
                {metric === METRIC_SENTIMENT
                    ? <FormattedMessage id="groupScores.legend_tone" defaultMessage="Average tone (−100% to +100%)" />
                    : <FormattedMessage id="groupScores.legend_score" defaultMessage="Signal score (clamped ±100%)" />}
            </Box>
            <Box component="span" sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.75 }}>
                <Box component="span" sx={{ width: 2, height: 14, background: `repeating-linear-gradient(to bottom, ${TEXT} 0 3px, transparent 3px 5px)`, opacity: 0.7 }} />
                <FormattedMessage id="groupScores.legend_baseline" defaultMessage="Whole-wave baseline" />
            </Box>
        </Box>
    );
}

// Heads every set of bars: the section label, the legend, and how to read it.
function BarsHeader({ metric, sx }) {
    return (
        <Box sx={{ mt: 3, mb: 1.5, ...sx }}>
            <SectionTitle action={<Legend metric={metric} />}>
                <FormattedMessage id="groupScores.bars_title" defaultMessage="Pillar scores" />
            </SectionTitle>
            <Typography variant="caption" component="p" sx={{ color: FAINT, mt: 0.25 }}>
                <FormattedMessage id="groupScores.subtitle" defaultMessage="Higher is better, centred at zero. The dashed marker is the whole wave." />
            </Typography>
        </Box>
    );
}

// The one row of controls above every view: selectors, then the view switch.
function ControlBar({ children }) {
    return (
        <Box
            data-testid="group-control-bar"
            sx={{ display: 'flex', gap: 1.5, flexWrap: 'wrap', alignItems: 'center', mt: 2.5, p: 1.5, bgcolor: TINT, border: `1px solid ${LINE}`, borderRadius: 3 }}
        >
            {children}
        </Box>
    );
}

// Shared by every selector in the bar: label always floated (a native select
// always has a value), white field on the tinted bar, one weight.
const SELECT_SX = {
    '& .MuiOutlinedInput-root': { bgcolor: '#fff' },
    '& select': { fontWeight: 600, color: INK, fontSize: 14 },
};

export const VIEW_SINGLE = 'single';
export const VIEW_COMPARE = 'compare';
export const VIEW_WAVES = 'waves';

// Every scored slice below the whole wave, as selector keys. Hidden (under-5)
// slices never reach the payload, so they can never be offered here.
const sliceKeys = (data) => [
    ...(data?.areas || []).map((_, i) => `area:${i}`),
    ...(data?.groups || []).map((_, i) => `group:${i}`),
];

// One view at a time: a single slice against the org, or two slices together.
function ViewSwitch({ view, onChange, groups, waves }) {
    const intl = useIntl();
    return (
        <ToggleButtonGroup
            exclusive
            size="small"
            value={view}
            onChange={(_, next) => { if (next) onChange(next); }}
            aria-label={intl.formatMessage({ id: 'groupScores.view_label', defaultMessage: 'View' })}
            sx={{ ...SWITCH_SX, ml: { md: 'auto' }, flexWrap: 'wrap' }}
        >
            <ToggleButton value={VIEW_SINGLE} data-testid="group-view-single">
                <FormattedMessage id="groupScores.view_single" defaultMessage="Single" />
            </ToggleButton>
            {groups && (
                <ToggleButton value={VIEW_COMPARE} data-testid="group-view-compare">
                    <FormattedMessage id="groupScores.view_compare" defaultMessage="Compare groups" />
                </ToggleButton>
            )}
            {waves && (
                <ToggleButton value={VIEW_WAVES} data-testid="group-view-waves">
                    <FormattedMessage id="groupScores.view_waves" defaultMessage="Compare waves" />
                </ToggleButton>
            )}
        </ToggleButtonGroup>
    );
}

// Areas then groups, as in the single view's selector, minus the whole wave.
// The slice picked on the other side is disabled, so A can never equal B.
function SliceSelect({ data, side, value, other, onChange }) {
    const intl = useIntl();
    const option = (key, row) => (
        <option key={key} value={key} disabled={key === other}>
            {`${row.name} (n=${intl.formatNumber(row.n)})`}
        </option>
    );
    return (
        <TextField
            select
            size="small"
            label={side === 'a'
                ? intl.formatMessage({ id: 'groupScores.compare_a', defaultMessage: 'Compare A' })
                : intl.formatMessage({ id: 'groupScores.compare_b', defaultMessage: 'with B' })}
            value={value}
            onChange={(e) => { if (e.target.value !== other) onChange(e.target.value); }}
            SelectProps={{ native: true }}
            InputLabelProps={{ shrink: true }}
            inputProps={{ 'data-testid': `group-compare-${side}` }}
            sx={{ ...SELECT_SX, flex: '1 1 240px', minWidth: 0, maxWidth: { md: 360 } }}
        >
            {(data.areas || []).length > 0 && (
                <optgroup label={intl.formatMessage({ id: 'groupScores.areas', defaultMessage: 'Areas' })}>
                    {data.areas.map((area, i) => option(`area:${i}`, area))}
                </optgroup>
            )}
            {(data.groups || []).length > 0 && (
                <optgroup label={intl.formatMessage({ id: 'groupScores.groups', defaultMessage: 'Groups' })}>
                    {data.groups.map((group, i) => option(`group:${i}`, group))}
                </optgroup>
            )}
        </TextField>
    );
}

// The A / B letter that ties each compare bar to its selector.
function SideBadge({ side, label, dark = side === 'a' }) {
    return (
        <Box
            component="span"
            sx={{
                display: 'inline-flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0,
                minWidth: 18, height: 18, px: label ? 0.6 : 0, borderRadius: 999, fontSize: 11, fontWeight: 700,
                bgcolor: dark ? '#16335c' : '#e7edf5', color: dark ? '#fff' : '#16335c',
            }}
        >
            {label || (side === 'a' ? 'A' : 'B')}
        </Box>
    );
}

// Two slices, pillar by pillar: A's bar over B's, each with its own value.
// No difference is computed — reading the pair is left to the viewer.
function CompareView({ data, metric, field, pillars, baselineScores, a, b }) {
    const intl = useIntl();
    const sides = [
        { side: 'a', key: a, row: resolveSelection(data, a).row },
        { side: 'b', key: b, row: resolveSelection(data, b).row },
    ];
    return (
        <Box data-testid="group-compare-view">
            <Box sx={{ display: 'flex', gap: { xs: 1.25, sm: 3 }, flexWrap: 'wrap', mt: 2.5, px: 2, py: 1.5, bgcolor: TINT, borderRadius: 3 }}>
                {sides.map(({ side, row }) => (
                    <Box key={side} data-testid={`group-compare-meta-${side}`} sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <SideBadge side={side} />
                        <Typography sx={{ fontSize: 15, fontWeight: 700, color: INK }}>{row.name}</Typography>
                        <Chip
                            size="small"
                            label={intl.formatMessage({ id: 'groupScores.n_respondents', defaultMessage: 'n = {n} respondents' }, { n: intl.formatNumber(row.n ?? 0) })}
                            sx={{ bgcolor: '#e7edf5', color: '#47566b' }}
                        />
                    </Box>
                ))}
            </Box>

            <BarsHeader metric={metric} />
            <Box>
                {pillars.map((pillar) => (
                    <Box
                        key={pillar}
                        data-testid={`group-compare-pillar-${pillar}`}
                        sx={{
                            display: 'grid',
                            gridTemplateColumns: COMPARE_GRID,
                            alignItems: 'center',
                            columnGap: { xs: 1.25, sm: 2 },
                            py: 1.25,
                            '& + &': { borderTop: '1px solid #f1f4f8' },
                        }}
                    >
                        <Typography variant="body2" sx={{ fontWeight: 600, color: TEXT, textAlign: 'right', lineHeight: 1.25 }}>
                            {pillar}
                        </Typography>
                        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.75 }}>
                            {sides.map(({ side, row }) => {
                                const value = (row[field] || {})[pillar];
                                return (
                                    <Box
                                        key={side}
                                        sx={{ display: 'grid', gridTemplateColumns: { xs: '18px minmax(0, 1fr) 70px', sm: '18px minmax(0, 150px) minmax(0, 1fr) 88px' }, alignItems: 'center', columnGap: 1.25 }}
                                    >
                                        <SideBadge side={side} />
                                        <Typography
                                            noWrap
                                            title={row.name}
                                            sx={{ display: { xs: 'none', sm: 'block' }, fontSize: 12, color: '#47566b' }}
                                        >
                                            {row.name}
                                        </Typography>
                                        <SignalTrack thin value={value} baseline={baselineScores[pillar]} />
                                        <ValuePill small value={value} testId={`group-compare-value-${side}-${pillar}`} />
                                    </Box>
                                );
                            })}
                        </Box>
                    </Box>
                ))}
            </Box>
        </Box>
    );
}

// Level selector: the whole wave, then areas, then groups — all from the
// report's own wave. The wave view reuses it, so its selection is always one
// this wave scored.
function LevelSelect({ data, value, onChange }) {
    const intl = useIntl();
    const wholeWave = intl.formatMessage({ id: 'groupScores.whole_wave', defaultMessage: 'All employees (whole wave)' });
    return (
        <TextField
            select
            size="small"
            label={intl.formatMessage({ id: 'groupScores.selector_label', defaultMessage: 'Show scores for' })}
            value={value}
            onChange={(e) => onChange(e.target.value)}
            SelectProps={{ native: true }}
            InputLabelProps={{ shrink: true }}
            inputProps={{ 'data-testid': 'group-scores-selector' }}
            sx={{ ...SELECT_SX, flex: { xs: '1 1 100%', sm: '0 1 360px' }, minWidth: 0 }}
        >
            <option value={BASELINE}>{`${wholeWave} (n=${intl.formatNumber(data.baseline?.n ?? 0)})`}</option>
            {(data.areas || []).length > 0 && (
                <optgroup label={intl.formatMessage({ id: 'groupScores.areas', defaultMessage: 'Areas' })}>
                    {data.areas.map((area, i) => (
                        <option key={`area-${area.name}`} value={`area:${i}`}>
                            {`${area.name} (n=${intl.formatNumber(area.n)})`}
                        </option>
                    ))}
                </optgroup>
            )}
            {(data.groups || []).length > 0 && (
                <optgroup label={intl.formatMessage({ id: 'groupScores.groups', defaultMessage: 'Groups' })}>
                    {data.groups.map((group, i) => (
                        <option key={`group-${group.name}`} value={`group:${i}`}>
                            {`${group.name} (n=${intl.formatNumber(group.n)})`}
                        </option>
                    ))}
                </optgroup>
            )}
        </TextField>
    );
}

// ── Wave view ────────────────────────────────────────────────────────────────
// The project's waves come from `getRagSources(reportId)` — the Ask Prompta
// selector's payload, `waves: [{id: <report id>, wave_count, label}]` oldest
// first — so the report page needs no project id of its own.

// The wave compared with this report by default: the one just before it, else
// the one just after. null when the project has no other wave.
export function defaultOtherWave(waves, reportId) {
    const current = (waves || []).find((w) => String(w.id) === String(reportId));
    const others = (waves || []).filter((w) => String(w.id) !== String(reportId));
    if (!others.length) return null;
    if (!current) return others[others.length - 1];
    const before = others.filter((w) => w.wave_count < current.wave_count);
    return before.length ? before[before.length - 1] : others[0];
}

// The same selection in another wave's payload, matched BY NAME: slices are
// indexed per payload, and a group can be new, dropped, or under 5 in one wave
// only. -> { row } when scored, { hidden } when that wave left it out, or {}.
export function matchSlice(payload, level, name) {
    if (!payload) return {};
    if (level === BASELINE) return { row: payload.baseline || {} };
    const rows = level === 'area' ? payload.areas : payload.groups;
    const row = (rows || []).find((r) => r.name === name);
    if (row) return { row };
    const hidden = (payload.hidden || []).find((h) => h.name === name && (!h.level || h.level === level));
    return hidden ? { hidden } : {};
}

function WaveSelect({ waves, reportId, value, onChange }) {
    const intl = useIntl();
    return (
        <TextField
            select
            size="small"
            label={intl.formatMessage({ id: 'groupScores.wave_picker', defaultMessage: 'Compare with' })}
            value={value ?? ''}
            onChange={(e) => onChange(e.target.value)}
            SelectProps={{ native: true }}
            InputLabelProps={{ shrink: true }}
            inputProps={{ 'data-testid': 'group-wave-picker' }}
            sx={{ ...SELECT_SX, flex: { xs: '1 1 100%', sm: '0 1 280px' }, minWidth: 0 }}
        >
            {waves.filter((w) => String(w.id) !== String(reportId)).map((w) => (
                <option key={w.id} value={String(w.id)}>{w.label}</option>
            ))}
        </TextField>
    );
}

// One selection across two waves, pillar by pillar, earlier wave on top.
// No difference is computed. A wave that did not score the selection keeps
// its place in the header with a note, and simply draws no bar.
function WaveCompareView({ sides, metric, field, pillars, level, name }) {
    const intl = useIntl();
    const nLabel = (n) => intl.formatMessage(
        { id: 'groupScores.n_respondents', defaultMessage: 'n = {n} respondents' },
        { n: intl.formatNumber(n ?? 0) },
    );
    const waveName = (wave) => intl.formatMessage(
        { id: 'groupScores.wave_short', defaultMessage: 'Wave {n}' },
        { n: wave.wave_count },
    );
    const scored = sides.filter((s) => s.row);
    return (
        <Box data-testid="group-wave-view">
            <Box sx={{ display: 'flex', gap: { xs: 1.25, sm: 3 }, flexWrap: 'wrap', mt: 2.5, px: 2, py: 1.5, bgcolor: TINT, borderRadius: 3 }}>
                {sides.map((s) => (
                    <Box key={s.wave.id} data-testid={`group-wave-meta-w${s.wave.wave_count}`} sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
                        <SideBadge label={`W${s.wave.wave_count}`} dark={s.primary} />
                        <Typography sx={{ fontSize: 15, fontWeight: 700, color: INK }}>{waveName(s.wave)}</Typography>
                        {s.row ? (
                            <Chip size="small" label={nLabel(s.row.n)} sx={{ bgcolor: '#e7edf5', color: '#47566b' }} />
                        ) : (
                            <Typography data-testid={`group-wave-absent-w${s.wave.wave_count}`} sx={{ fontSize: 12.5, color: '#b56c14', fontStyle: 'italic' }}>
                                {s.hidden && s.hidden.reason !== NO_MEMBERS ? (
                                    <FormattedMessage
                                        id="groupScores.wave_hidden"
                                        defaultMessage="{name} is hidden in this wave — fewer than 5 respondents"
                                        values={{ name }}
                                    />
                                ) : (
                                    <FormattedMessage
                                        id="groupScores.wave_absent"
                                        defaultMessage="{name} is not scored in this wave"
                                        values={{ name }}
                                    />
                                )}
                            </Typography>
                        )}
                    </Box>
                ))}
            </Box>

            {scored.length > 0 && (<>
                <BarsHeader metric={metric} />
                <Box>
                    {pillars.map((pillar) => (
                        <Box
                            key={pillar}
                            data-testid={`group-wave-pillar-${pillar}`}
                            sx={{
                                display: 'grid',
                                gridTemplateColumns: COMPARE_GRID,
                                alignItems: 'center',
                                columnGap: { xs: 1.25, sm: 2 },
                                py: 1.25,
                                '& + &': { borderTop: '1px solid #f1f4f8' },
                            }}
                        >
                            <Typography variant="body2" sx={{ fontWeight: 600, color: TEXT, textAlign: 'right', lineHeight: 1.25 }}>
                                {pillar}
                            </Typography>
                            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.75 }}>
                                {scored.map((s) => {
                                    const value = (s.row[field] || {})[pillar];
                                    const baseline = level === BASELINE ? undefined : (s.payload.baseline?.[field] || {})[pillar];
                                    return (
                                        <Box
                                            key={s.wave.id}
                                            sx={{ display: 'grid', gridTemplateColumns: { xs: '30px minmax(0, 1fr) 70px', sm: '30px minmax(0, 1fr) 88px' }, alignItems: 'center', columnGap: 1.25 }}
                                        >
                                            <SideBadge label={`W${s.wave.wave_count}`} dark={s.primary} />
                                            <SignalTrack thin value={value} baseline={baseline} />
                                            <ValuePill small value={value} testId={`group-wave-value-w${s.wave.wave_count}-${pillar}`} />
                                        </Box>
                                    );
                                })}
                            </Box>
                        </Box>
                    ))}
                </Box>
            </>)}
        </Box>
    );
}

export default function GroupScoresSection({ reportId, data: preloaded }) {
    const intl = useIntl();
    const [data, setData] = useState(preloaded || null);
    const [loading, setLoading] = useState(!preloaded);
    const [error, setError] = useState(false);
    const [selected, setSelected] = useState(BASELINE);
    const [metricChoice, setMetricChoice] = useState(METRIC_RISK);
    const [view, setView] = useState(VIEW_SINGLE);
    const [compare, setCompare] = useState({ a: null, b: null });
    const [waves, setWaves] = useState(null);
    const [otherWaveId, setOtherWaveId] = useState(null);
    // Other waves' payloads by report id: { loading } | { data } | { error }.
    const [waveScores, setWaveScores] = useState({});
    // The in-card chat's threads, one per selection (`reportId:scope_key`),
    // and which selections have the chat opened (closed by default).
    const chats = useScopedChats(reportId);
    const [openChats, setOpenChats] = useState({});
    // "What to do next", by `reportId:scope_key`.
    const [groupActions, setGroupActions] = useState({});
    const updateGroupActions = (key, fn) => setGroupActions((prev) => ({ ...prev, [key]: fn(prev[key]) }));
    const loadGroupActions = (scopeKey) => {
        const key = threadKey(reportId, scopeKey);
        updateGroupActions(key, () => ({ loading: true }));
        insightsApi.getGroupActions(reportId, scopeKey)
            .then((result) => updateGroupActions(key, () => freshActionsEntry(result)))
            .catch((err) => updateGroupActions(key, () => ({ error: { status: err?.response?.status ?? null } })));
    };
    // "Impacts & activities", by `reportId:scope_key`, and which are expanded.
    const [groupImpacts, setGroupImpacts] = useState({});
    const [openImpacts, setOpenImpacts] = useState({});
    const loadGroupImpacts = (scopeKey) => {
        const key = threadKey(reportId, scopeKey);
        setGroupImpacts((prev) => ({ ...prev, [key]: { loading: true } }));
        insightsApi.getGroupImpacts(reportId, scopeKey)
            .then((result) => setGroupImpacts((prev) => ({ ...prev, [key]: { data: result } })))
            .catch((err) => setGroupImpacts((prev) => ({ ...prev, [key]: { error: { status: err?.response?.status ?? null } } })));
    };
    // The compare views' AI insights, by comparePairKey.
    const [compareInsights, setCompareInsights] = useState({});
    const loadCompareInsight = (body) => {
        const key = comparePairKey(reportId, body);
        setCompareInsights((prev) => ({ ...prev, [key]: { loading: true } }));
        insightsApi.getCompareInsight(reportId, body)
            .then((result) => setCompareInsights((prev) => ({ ...prev, [key]: { data: result } })))
            .catch((err) => setCompareInsights((prev) => ({
                ...prev,
                [key]: { error: { status: err?.response?.status ?? null } },
            })));
    };
    // The panel for one compare pair; `body` null means it cannot be asked.
    const compareInsightFor = (body, disabledReason) => (
        <CompareInsight
            state={body ? compareInsights[comparePairKey(reportId, body)] : undefined}
            body={body}
            disabledReason={disabledReason}
            onLoad={() => body && loadCompareInsight(body)}
        />
    );

    // The report page already fetched the payload to decide whether this tab
    // exists at all; only fetch here when rendered on its own.
    useEffect(() => {
        if (preloaded) {
            setData(preloaded);
            setLoading(false);
            return undefined;
        }
        let cancelled = false;
        setLoading(true);
        setError(false);
        insightsApi.getGroupScores(reportId)
            .then((payload) => { if (!cancelled) setData(payload); })
            .catch(() => { if (!cancelled) setError(true); })
            .finally(() => { if (!cancelled) setLoading(false); });
        return () => { cancelled = true; };
    }, [reportId, preloaded]);

    // The project's waves, fetched with the tab. A report outside any project
    // (404) or a failure just leaves the wave view out.
    useEffect(() => {
        if (reportId == null) return undefined;
        let cancelled = false;
        setWaves(null);
        setOtherWaveId(null);
        setWaveScores({});
        insightsApi.getRagSources(reportId)
            .then((sources) => { if (!cancelled) setWaves(sources?.waves || []); })
            .catch(() => { if (!cancelled) setWaves([]); });
        return () => { cancelled = true; };
    }, [reportId]);

    const otherWave = useMemo(() => {
        const picked = (waves || []).find((w) => String(w.id) === String(otherWaveId));
        return picked && String(picked.id) !== String(reportId) ? picked : defaultOtherWave(waves, reportId);
    }, [waves, otherWaveId, reportId]);

    // The other wave's scores load only once the wave view asks for them.
    const needOther = view === VIEW_WAVES && otherWave && !waveScores[otherWave.id];
    useEffect(() => {
        if (!needOther) return;
        const id = otherWave.id;
        setWaveScores((prev) => ({ ...prev, [id]: { loading: true } }));
        insightsApi.getGroupScores(id)
            .then((payload) => setWaveScores((prev) => ({ ...prev, [id]: { data: payload } })))
            .catch(() => setWaveScores((prev) => ({ ...prev, [id]: { error: true } })));
    }, [needOther, otherWave]);

    const selection = useMemo(() => resolveSelection(data, selected), [data, selected]);

    if (loading) {
        return (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
                <CircularProgress />
            </Box>
        );
    }
    if (error || !data) {
        return (
            <Alert severity="error">
                <FormattedMessage id="groupScores.load_failed" defaultMessage="Failed to load the group scores." />
            </Alert>
        );
    }

    // The tab is shown from the group registry, which cannot know slice sizes:
    // every group may still come back hidden. Say so rather than draw nothing.
    if (!hasGroupSlices(data)) {
        return (
            <Card sx={{ mb: 3 }} data-testid="group-scores-empty">
                <CardContent>
                    <Heading />
                    <Alert severity="info" sx={{ mt: 2 }}>
                        <FormattedMessage id="groupScores.empty_all_hidden" defaultMessage="No area or group in this report has enough respondents to show its scores." />
                    </Alert>
                    <HiddenCaptions hidden={data.hidden} />
                </CardContent>
            </Card>
        );
    }

    const keys = sliceKeys(data);
    const canCompare = keys.length >= 2;
    const canCompareWaves = Boolean(otherWave);
    const comparing = canCompare && view === VIEW_COMPARE;
    const wavesView = canCompareWaves && view === VIEW_WAVES;
    const single = !comparing && !wavesView;
    const other = wavesView ? (waveScores[otherWave.id] || { loading: true }) : null;

    // Tone across waves needs tone in BOTH; until the other wave lands, it is
    // not known to have it, so the switch holds on risk.
    const sentimentAvailable = Boolean(data.has_sentiment) && (!wavesView || Boolean(other.data?.has_sentiment));
    const metric = METRIC_SWITCH_ENABLED && metricChoice === METRIC_SENTIMENT && sentimentAvailable
        ? METRIC_SENTIMENT
        : METRIC_RISK;
    const field = METRIC_FIELD[metric];
    const baselineScores = data.baseline?.[field] || {};
    const pillars = data.pillars?.length ? data.pillars : Object.keys(baselineScores);
    const { level, row } = selection;
    const isBaseline = level === BASELINE;
    const nLabel = (n) => intl.formatMessage(
        { id: 'groupScores.n_respondents', defaultMessage: 'n = {n} respondents' },
        { n: intl.formatNumber(n ?? 0) },
    );
    const wholeWave = intl.formatMessage({ id: 'groupScores.whole_wave', defaultMessage: 'All employees (whole wave)' });

    const topics = row.top_topics || [];
    const focusAreas = focusAreasOf(row);

    // Compare needs two slices. A opens on the single view's pick when that is
    // a slice, else the first one; B on the first slice that is not A. A stale
    // or clashing key (a new payload) falls back the same way.
    const seedA = keys.includes(selected) ? selected : keys[0];
    const compareA = keys.includes(compare.a) ? compare.a : seedA;
    const compareB = keys.includes(compare.b) && compare.b !== compareA
        ? compare.b
        : keys.find((key) => key !== compareA);

    return (
        <Card sx={{ mb: 3 }}>
            <CardContent>
                <Heading metric={metric} />

                {/* One control bar: what to show, then how to view it */}
                <ControlBar>
                    {single && <LevelSelect data={data} value={selected} onChange={setSelected} />}
                    {comparing && (<>
                        <SliceSelect data={data} side="a" value={compareA} other={compareB} onChange={(a) => setCompare({ a, b: compareB })} />
                        <SliceSelect data={data} side="b" value={compareB} other={compareA} onChange={(b) => setCompare({ a: compareA, b })} />
                    </>)}
                    {wavesView && (<>
                        <LevelSelect data={data} value={selected} onChange={setSelected} />
                        <WaveSelect waves={waves} reportId={reportId} value={String(otherWave.id)} onChange={setOtherWaveId} />
                    </>)}
                    {METRIC_SWITCH_ENABLED && (
                        <MetricSwitch
                            metric={metric}
                            available={sentimentAvailable}
                            onChange={setMetricChoice}
                            unavailableReason={wavesView && data.has_sentiment
                                ? intl.formatMessage({ id: 'groupScores.sentiment_unavailable_waves', defaultMessage: 'Needs tone in both waves — not available for the wave compared yet' })
                                : undefined}
                        />
                    )}
                    {(canCompare || canCompareWaves) && (
                        <ViewSwitch view={view} onChange={setView} groups={canCompare} waves={canCompareWaves} />
                    )}
                </ControlBar>

                {comparing && (
                    <CompareView
                        data={data}
                        metric={metric}
                        field={field}
                        pillars={pillars}
                        baselineScores={baselineScores}
                        a={compareA}
                        b={compareB}
                    />
                )}
                {comparing && (() => {
                    const scopeA = resolveSelection(data, compareA).row.scope_key;
                    const scopeB = resolveSelection(data, compareB).row.scope_key;
                    return compareInsightFor(
                        scopeA && scopeB ? { mode: 'groups', scope_a: scopeA, scope_b: scopeB } : null,
                        scopeA && scopeB ? undefined : intl.formatMessage({ id: 'groupScores.compare_insight_no_scope', defaultMessage: 'Not available for this report yet.' }),
                    );
                })()}

                {wavesView && (<>
                    {other.loading && (
                        <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
                            <CircularProgress size={28} data-testid="group-wave-loading" />
                        </Box>
                    )}
                    {other.error && (
                        <Alert severity="error" sx={{ mt: 2.5 }} data-testid="group-wave-error">
                            <FormattedMessage id="groupScores.wave_load_failed" defaultMessage="Failed to load the scores for the wave compared." />
                        </Alert>
                    )}
                    {other.data && (() => {
                        const thisWave = (waves || []).find((w) => String(w.id) === String(reportId))
                            || { id: reportId, wave_count: '?' };
                        const name = isBaseline ? wholeWave : row.name;
                        const sides = [
                            { wave: thisWave, primary: true, payload: data, row },
                            { wave: otherWave, primary: false, payload: other.data, ...matchSlice(other.data, level, row.name) },
                        ].sort((x, y) => Number(x.wave.wave_count) - Number(y.wave.wave_count));
                        const otherSide = sides.find((side) => !side.primary);
                        const otherId = Number(otherWave.id);
                        let disabledReason;
                        if (!row.scope_key || !Number.isInteger(otherId)) {
                            disabledReason = intl.formatMessage({ id: 'groupScores.compare_insight_no_scope', defaultMessage: 'Not available for this report yet.' });
                        } else if (!otherSide.row) {
                            disabledReason = otherSide.hidden && otherSide.hidden.reason !== NO_MEMBERS
                                ? intl.formatMessage(
                                    { id: 'groupScores.compare_insight_hidden', defaultMessage: '{name} is hidden in Wave {n} (fewer than 5 respondents).' },
                                    { name, n: otherWave.wave_count },
                                )
                                : intl.formatMessage(
                                    { id: 'groupScores.compare_insight_absent', defaultMessage: '{name} is not scored in Wave {n}.' },
                                    { name, n: otherWave.wave_count },
                                );
                        }
                        return (<>
                            <WaveCompareView sides={sides} metric={metric} field={field} pillars={pillars} level={level} name={name} />
                            {compareInsightFor(
                                disabledReason ? null : { mode: 'waves', scope_a: row.scope_key, other_report_id: otherId },
                                disabledReason,
                            )}
                        </>);
                    })()}
                </>)}

                {single && (<>
                {/* Meta strip: name · area · n · risk */}
                <Box
                    data-testid="group-scores-meta"
                    sx={{ display: 'flex', columnGap: 1.5, rowGap: 1, flexWrap: 'wrap', alignItems: 'center', mt: 2.5, px: 2, py: 1.5, bgcolor: TINT, borderRadius: 3 }}
                >
                    <Typography sx={{ fontSize: 17, fontWeight: 700, color: INK, mr: 0.5 }}>
                        {isBaseline ? wholeWave : row.name}
                    </Typography>
                    {level === 'area' && (
                        <Chip size="small" label={intl.formatMessage({ id: 'groupScores.level_area', defaultMessage: 'Area' })} sx={{ bgcolor: '#e7edf5', color: '#47566b' }} />
                    )}
                    {level === 'group' && row.area && (
                        <Chip size="small" label={row.area} sx={{ bgcolor: '#e7edf5', color: '#47566b' }} />
                    )}
                    <Chip size="small" label={nLabel(row.n)} sx={{ ...NUMERIC, bgcolor: '#e7edf5', color: '#47566b' }} />
                    {metric === METRIC_RISK && row.risk && (
                        <Typography sx={{ fontSize: 12.5, fontWeight: 700, color: riskColor(row.risk) }}>
                            {row.risk}
                        </Typography>
                    )}
                    {level === 'area' && (row.groups || []).length > 0 && (
                        <Typography sx={{ flexBasis: '100%', fontSize: 12.5, color: MUTED }}>
                            <FormattedMessage
                                id="groupScores.area_members"
                                defaultMessage="Groups: {names}"
                                values={{ names: row.groups.join(', ') }}
                            />
                        </Typography>
                    )}
                </Box>

                {/* The six pillars, against the whole wave */}
                <BarsHeader metric={metric} sx={{ mt: 3.5 }} />
                <Box sx={{ pt: isBaseline ? 0 : 1 }}>
                    {pillars.map((pillar, i) => (
                        <PillarRow
                            key={pillar}
                            pillar={pillar}
                            value={(row[field] || {})[pillar]}
                            baseline={isBaseline ? undefined : baselineScores[pillar]}
                            markerLabel={i === 0}
                        />
                    ))}
                </Box>

                {/* The AI read of the chart above, with the chat behind a button */}
                {(focusAreas.length > 0 || row.scope_key) && (() => {
                    const key = row.scope_key ? threadKey(reportId, row.scope_key) : null;
                    const chat = key && (
                        <ChatToggle
                            open={Boolean(openChats[key])}
                            onToggle={() => setOpenChats((prev) => ({ ...prev, [key]: !prev[key] }))}
                        >
                            <GroupScoresChat
                                key={key}
                                name={isBaseline ? wholeWave : row.name}
                                thread={chats.threads[key]}
                                onAsk={(question) => chats.ask(row.scope_key, question, historyOf(chats.threads[key]))}
                                sx={{ bgcolor: '#fff' }}
                            />
                        </ChatToggle>
                    );
                    return (
                        <Box data-testid="group-ai-section" sx={{ mt: 3 }}>
                            {focusAreas.length > 0 ? <FocusAreasCard items={focusAreas}>{chat}</FocusAreasCard> : chat}
                        </Box>
                    );
                })()}

                {/* What to do next: recommended actions, pushable to the Activity Dashboard */}
                {row.scope_key && (() => {
                    const key = threadKey(reportId, row.scope_key);
                    const entry = groupActions[key];
                    return (
                        <GroupActionsCard
                            key={`actions:${key}`}
                            reportId={reportId}
                            scopeKey={row.scope_key}
                            entry={entry}
                            onLoad={() => loadGroupActions(row.scope_key)}
                            update={(fn) => updateGroupActions(key, fn)}
                        />
                    );
                })()}

                {/* Impacts & activities: the selection's Register rows, collapsed by default */}
                {row.scope_key && (() => {
                    const key = threadKey(reportId, row.scope_key);
                    const entry = groupImpacts[key];
                    return (
                        <GroupImpactsPanel
                            key={`impacts:${key}`}
                            reportId={reportId}
                            scopeKey={row.scope_key}
                            entry={entry}
                            onLoad={() => loadGroupImpacts(row.scope_key)}
                            open={Boolean(openImpacts[key])}
                            onToggle={() => setOpenImpacts((prev) => ({ ...prev, [key]: !prev[key] }))}
                        />
                    );
                })()}
                </>)}

                {/* Notable themes: over/under-representation vs the whole wave */}
                {single && (<>
                <Box sx={SECTION_SX}>
                    <SectionTitle sx={{ mb: 1.25 }}>
                        <FormattedMessage id="groupScores.themes_title" defaultMessage="Notable themes vs. the whole wave" />
                    </SectionTitle>
                    {isBaseline ? (
                        <Typography variant="body2" sx={{ color: FAINT }}>
                            <FormattedMessage id="groupScores.themes_baseline" defaultMessage="This is the whole-wave baseline — pick an area or group to see which themes it over- or under-represents." />
                        </Typography>
                    ) : topics.length === 0 ? (
                        <Typography variant="body2" sx={{ color: FAINT }}>
                            <FormattedMessage id="groupScores.themes_empty" defaultMessage="No theme movements for this selection." />
                        </Typography>
                    ) : (
                        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                            {topics.map((topic) => {
                                const tone = TONES[topic.value >= 0 ? 'pos' : 'neg'];
                                return (
                                    <Box
                                        key={`${topic.pillar}-${topic.name}`}
                                        sx={{ display: 'inline-flex', alignItems: 'center', gap: 1.25, bgcolor: '#fff', border: `1px solid ${LINE}`, borderRadius: 2, px: 1.5, py: 0.9, fontSize: 13, maxWidth: '100%' }}
                                    >
                                        <Box component="span" sx={{ color: TEXT, maxWidth: 420, minWidth: 0 }}>{topic.name}</Box>
                                        <Box component="span" sx={{ ...NUMERIC, fontWeight: 700, fontSize: 12, px: 1, py: 0.25, borderRadius: 999, bgcolor: tone.pillBg, color: tone.pillFg, whiteSpace: 'nowrap' }}>
                                            <FormattedMessage
                                                id="groupScores.theme_points"
                                                defaultMessage="{value} pts"
                                                values={{ value: signedText(topic.value, 1) }}
                                            />
                                        </Box>
                                    </Box>
                                );
                            })}
                        </Box>
                    )}
                </Box>
                </>)}
                <HiddenCaptions hidden={data.hidden} />

                {single && <GapsSummary payload={data} metric={metric} />}
            </CardContent>
        </Card>
    );
}
